import React from 'react';
import { Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatTimeRange12h } from '../../utils/timeFormat';

export function attendanceStatusLabel(status) {
  if (!status) return 'Not marked';
  const s = String(status).toLowerCase();
  if (s === 'present') return 'Present';
  if (s === 'absent') return 'Absent';
  if (s === 'late') return 'Late';
  if (s === 'half_day') return 'Half day';
  return status;
}

export function attendanceStatusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'present') return 'bg-green-500/15 text-green-700 dark:text-green-300';
  if (s === 'absent') return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
  if (s === 'late') return 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-300';
  return 'bg-muted text-muted-foreground';
}

export function SchoolHoursCard({ hours }) {
  if (!hours?.start_time || !hours?.end_time) return null;
  return (
    <Card className="border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-foreground flex items-center gap-2 text-base">
          <Clock className="w-4 h-4" /> School hours
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">{hours.schedule_name || 'Schedule'}</p>
        <p className="text-lg font-semibold text-foreground mt-1">
          {formatTimeRange12h(hours.start_time, hours.end_time)}
        </p>
      </CardContent>
    </Card>
  );
}

export function TodayAttendanceCard({ todayAttendance }) {
  const status = todayAttendance?.status;
  return (
    <Card className="border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-foreground text-base">Today&apos;s attendance</CardTitle>
      </CardHeader>
      <CardContent>
        <span className={`inline-flex text-sm font-medium px-2.5 py-1 rounded-full ${attendanceStatusClass(status)}`}>
          {attendanceStatusLabel(status)}
        </span>
      </CardContent>
    </Card>
  );
}

/**
 * Compact summary for portal pages (Schedule, Attendance) on mobile.
 */
export default function PortalScheduleSummary({ schoolHours, todayAttendance }) {
  const hasHours = schoolHours?.start_time && schoolHours?.end_time;
  if (!hasHours && todayAttendance == null) return null;

  return (
    <Card className="border-border mb-4">
      <CardContent className="pt-4 pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {hasHours ? (
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-9 h-9 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
                <Clock className="w-4 h-4 text-blue-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">School hours</p>
                <p className="font-semibold text-foreground">
                  {formatTimeRange12h(schoolHours.start_time, schoolHours.end_time)}
                </p>
                {schoolHours.schedule_name ? (
                  <p className="text-xs text-muted-foreground truncate">{schoolHours.schedule_name}</p>
                ) : null}
              </div>
            </div>
          ) : null}
          {todayAttendance !== undefined ? (
            <div className={hasHours ? 'sm:text-right' : ''}>
              <p className="text-xs text-muted-foreground mb-1">Today</p>
              <span className={`inline-flex text-sm font-medium px-2.5 py-1 rounded-full ${attendanceStatusClass(todayAttendance?.status)}`}>
                {attendanceStatusLabel(todayAttendance?.status)}
              </span>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
